import { ref } from 'vue'
import { Share } from '@capacitor/share'
import { Capacitor } from '@capacitor/core'
import { useI18n } from 'vue-i18n'

const APP_LINK_HOST = 'https://app.hockeybook.online'

export interface ShareLinkOptions {
  /** Path part after host, e.g. `/event/123` or `/teams/abc?tab=info`. Must start with `/`. */
  path: string | (() => string)
  /** Optional dynamic title (e.g. event name). */
  title?: string | (() => string)
}

/**
 * Reusable share-link composable. Handles native Capacitor Share, web
 * navigator.share, and clipboard fallback. Returns a `share()` function and
 * a `showSnackbar` ref for the "link copied" toast.
 */
export function useShareLink(options: ShareLinkOptions) {
  const { t } = useI18n()
  const showSnackbar = ref(false)

  const resolve = <T>(v: T | (() => T)): T => (typeof v === 'function' ? (v as () => T)() : v)

  async function share() {
    const path = resolve(options.path)
    const url = `${APP_LINK_HOST}${path.startsWith('/') ? path : `/${path}`}`
    const title = options.title ? resolve(options.title) : t('share')

    if (Capacitor.isNativePlatform()) {
      try {
        await Share.share({ title, url, dialogTitle: t('share') })
      } catch {
        /* user dismissed */
      }
      return
    }

    try {
      if (typeof navigator !== 'undefined' && (navigator as any).share) {
        await (navigator as any).share({ title, url })
        return
      }
    } catch {
      /* fall through to clipboard */
    }

    try {
      await navigator.clipboard.writeText(url)
      showSnackbar.value = true
    } catch {
      /* ignore */
    }
  }

  return { share, showSnackbar }
}
